import React, { useState } from 'react';

const CONFIDENCE = {
  high: 'bg-amber-100 text-amber-900',
  medium: 'bg-slate-100 text-slate-700',
  low: 'bg-slate-50 text-slate-500',
};

const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'date_of_birth', label: 'Geburtsdatum' },
  { key: 'email', label: 'E-Mail' },
  { key: 'phone', label: 'Telefonnummer' },
  { key: 'address', label: 'Anschrift' },
  { key: 'iban_last4', label: 'Bankverbindung' },
];

function displayValue(field, value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (field.key === 'date_of_birth') return `geb. ${text}`;
  if (field.key === 'iban_last4') return `IBAN …${text}`;
  return text;
}

function comparable(field, value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (field.key === 'email' || field.key === 'name' || field.key === 'address') {
    return text.toLowerCase().replace(/\s+/g, ' ');
  }
  if (field.key === 'phone') return text.replace(/\D/g, '');
  return text;
}

function personName(data) {
  if (!data) return '';
  return data.name || [data.first_name, data.last_name].filter(Boolean).join(' ');
}

function fieldRows(match) {
  return FIELDS.map((field) => {
    const existingRaw = field.key === 'name' ? personName(match.existing) : match.existing?.[field.key];
    const incomingRaw = field.key === 'name' ? personName(match.incoming) : match.incoming?.[field.key];
    const existing = displayValue(field, existingRaw);
    const incoming = displayValue(field, incomingRaw);
    return {
      ...field,
      existing,
      incoming,
      differs: comparable(field, match.existing?.[field.key]) !== comparable(field, match.incoming?.[field.key])
        && Boolean(existing || incoming),
      shown: Boolean(existing || incoming),
    };
  }).filter((row) => row.shown);
}

function matchKey(match) {
  return `${match.entity_type}-${match.entity_id}-${match.kind}`;
}

export default function RegistrationMatchPanel({
  matches,
  onSelectMatch,
  onChooseNew,
  selectedStudentId,
  selectedGuardianId,
  chooseNew = false,
}) {
  const [choices, setChoices] = useState({});
  const [pendingKey, setPendingKey] = useState(null);
  if (!matches?.length) return null;

  const choiceFor = (match, fieldKey) => choices[`${matchKey(match)}:${fieldKey}`] || 'existing';
  const updatesFor = (match) => fieldRows(match)
    .filter((row) => row.differs && choiceFor(match, row.key) === 'new')
    .map((row) => ({ key: row.key, label: row.label, value: row.incoming }));
  const choose = (match, fieldKey, source) => {
    setChoices((prev) => ({ ...prev, [`${matchKey(match)}:${fieldKey}`]: source }));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="registration-person-matches">
      <div>
        <div className="font-semibold text-slate-900">Möglicherweise bereits vorhandene Person/Familie</div>
        <p className="text-xs text-slate-600 mt-0.5">
          Keine automatische Zusammenführung. Bei abweichenden Angaben wird je Feld entschieden, welcher Wert gespeichert wird. Gleiche Angaben bleiben unverändert.
        </p>
      </div>
      {matches.map((m) => {
        const rows = fieldRows(m);
        const kept = rows.map((row) => ({
          label: row.label,
          value: !row.differs || choiceFor(m, row.key) === 'existing' ? row.existing : row.incoming,
        })).filter((row) => row.value);
        return (
          <div
            key={matchKey(m)}
            className="rounded-md border border-slate-200 p-2"
            data-testid={`registration-match-${m.kind}-${m.entity_id}`}
          >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-medium text-slate-900">{m.kind_label}</span>
              {m.role_label && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`registration-match-role-${m.entity_id}`}>
                  {m.role_label}
                </span>
              )}
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${CONFIDENCE[m.confidence] || CONFIDENCE.medium}`}>
                {m.confidence === 'high' ? 'hohe Übereinstimmung' : m.confidence === 'medium' ? 'mögliche Übereinstimmung' : 'ähnlich'}
              </span>
            </div>
            <div className="grid grid-cols-[7rem_1fr_1fr] gap-x-3 text-xs text-slate-500 mb-1">
              <span />
              <span>Vorhandene Daten in MAMV</span>
              <span>Neue Daten aus der Anmeldung</span>
            </div>
            <div className="space-y-1">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className={`grid grid-cols-[7rem_1fr_1fr] gap-x-3 gap-y-1 rounded px-1 py-1 text-xs ${row.differs ? 'bg-amber-50' : ''}`}
                  data-testid={`registration-match-field-${row.key}`}
                  data-differs={row.differs ? 'true' : 'false'}
                >
                  <div className="text-slate-500">{row.label}</div>
                  <div className="text-slate-800">{row.existing || '—'}</div>
                  <div className="text-slate-800">{row.incoming || '—'}</div>
                  {row.differs && (
                    <div className="col-span-3 flex flex-wrap gap-3 text-slate-700">
                      <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name={`${matchKey(m)}-${row.key}`}
                          checked={choiceFor(m, row.key) === 'existing'}
                          onChange={() => choose(m, row.key, 'existing')}
                        />
                        Bisherigen Wert behalten
                      </label>
                      <label className="inline-flex items-center gap-1">
                        <input
                          type="radio"
                          name={`${matchKey(m)}-${row.key}`}
                          checked={choiceFor(m, row.key) === 'new'}
                          onChange={() => choose(m, row.key, 'new')}
                        />
                        Neuen Wert übernehmen
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-700" data-testid="registration-match-kept">
              Anschließend gespeichert: {kept.map((row) => `${row.label} ${row.value}`).join(' · ') || '—'}
            </p>
            {m.linkable !== false && onSelectMatch && ['student', 'guardian'].includes(m.entity_type) && (
              <button
                type="button"
                className={`mt-2 rounded border px-2 py-1 text-xs font-medium ${
                  (m.entity_type === 'student' ? selectedStudentId : selectedGuardianId) === m.entity_id
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => setPendingKey(matchKey(m))}
              >
                Vorhandene Person verknüpfen
              </button>
            )}
            {pendingKey === matchKey(m) && (
              <div className="mt-2 rounded border border-slate-300 bg-slate-50 p-2 text-xs text-slate-800" data-testid="registration-match-confirm">
                <p>
                  {updatesFor(m).length
                    ? `Folgende Daten werden aktualisiert: ${updatesFor(m).map((row) => `${row.label} ${row.value}`).join(', ')}.`
                    : 'Folgende Daten werden aktualisiert: keine. Anschrift und Bankverbindung bleiben bestehen.'}
                  {' '}Bestehende Verträge bleiben unverändert.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="rounded border border-emerald-600 bg-emerald-50 px-2 py-1 font-medium text-emerald-800"
                    onClick={() => onSelectMatch(m, updatesFor(m))}
                  >
                    Verknüpfung durchführen
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-slate-700"
                    onClick={() => setPendingKey(null)}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {onChooseNew && (
        <button
          type="button"
          className={`rounded border px-2 py-1 text-xs font-medium ${
            chooseNew
              ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
          onClick={onChooseNew}
        >
          Als neue Person anlegen
        </button>
      )}
    </div>
  );
}
